// i18next's bound `t` on the instance src/i18n initialises at app start.
import { t as translate } from 'i18next';
import { classifyChatError } from '../lib/chat-error-kind';
import type { MutableRefObject } from 'react';
import type { ChatModelAdapter } from '@assistant-ui/react';
import type {
  AgentChatCapabilities,
  AgentEventDisplay,
  AgentMode,
  ArtifactContext,
  ArtifactDraftEvent,
  PageContextEnvelope,
} from '@tabbio/types';
import { buildToolActivity, streamAgentChat, type AgentStreamEvent, type AgentToolActivity, type JSONObject, type JSONValue } from './agent-stream';
import { bumpNativeAgentDebugCounter, logNativeAgentDebug } from '@/lib/agent-stream-debug';
import { trackProductEvent } from '@/lib/product-analytics';
import { resolveDeviceTimezone } from '@/lib/device-timezone';
import { sitePageArtifactId } from '@/lib/site-compose';
import {
  applyArtifactDraftEvent,
  markArtifactDraftResult,
  stopArtifactDrafts,
} from '@/components/artifacts/artifact-draft-store';
import { rememberSentAttachments, sentMessageAttachments } from '@/lib/agent-attachments';
import {
  noteArtifactEditCall,
  noteArtifactEditResult,
  stopArtifactEdits,
} from '@/components/artifacts/artifact-edit-activity';

type RuntimeToolCallPart = {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: JSONObject;
  argsText: string;
  result?: JSONValue;
  isError?: boolean;
  display?: AgentEventDisplay;
  category?: string;
  entityType?: string;
  entityId?: string;
  stage?: AgentToolActivity['stage'];
  approvalId?: string;
  runId?: string;
  status?: string;
  cardType?: string;
};

type RuntimeContentPart =
  | {
      type: 'text';
      text: string;
      streamKind?: 'tail';
    }
  | {
      type: 'text';
      text: string;
      streamKind: 'tail';
    }
  | RuntimeToolCallPart;

function findTextPart(content: RuntimeContentPart[]) {
  return content.find((part): part is Extract<RuntimeContentPart, { type: 'text' }> => {
    return part.type === 'text' && part.streamKind !== 'tail';
  });
}

function findStreamTextPart(content: RuntimeContentPart[]) {
  return content.find((part): part is Extract<RuntimeContentPart, { type: 'text'; streamKind: 'tail' }> => {
    return part.type === 'text' && part.streamKind === 'tail';
  });
}

function upsertToolPart(content: RuntimeContentPart[], activity: AgentToolActivity) {
  const nextPart: RuntimeToolCallPart = {
    type: 'tool-call',
    toolCallId: activity.toolCallId,
    toolName: activity.toolName,
    args: activity.args,
    argsText: activity.argsText,
    result: activity.result,
    isError: activity.isError,
    display: activity.displayDetail,
    category: activity.category,
    entityType: activity.entityType,
    entityId: activity.entityId,
    stage: activity.stage,
    approvalId: activity.approvalId,
    runId: activity.runId,
    status: activity.status,
    cardType: activity.cardType,
  };

  const existingIndex = content.findIndex((part) => {
    return part.type === 'tool-call' && part.toolCallId === activity.toolCallId;
  });

  if (existingIndex === -1) {
    content.push(nextPart);
    return;
  }

  const existingPart = content[existingIndex];
  content[existingIndex] = {
    ...existingPart,
    ...nextPart,
    args: activity.eventType === 'tool-result' && existingPart.type === 'tool-call' ? existingPart.args : nextPart.args,
    argsText: activity.eventType === 'tool-result' && existingPart.type === 'tool-call' ? existingPart.argsText : nextPart.argsText,
  };
}

/** Sort content so tool-call parts always come before text parts (cards are the hero). */
function sortedContent(content: RuntimeContentPart[]): RuntimeContentPart[] {
  const priority: Record<RuntimeContentPart['type'], number> = {
    'tool-call': 0,
    text: 1,
  };

  return [...content].sort((a, b) => {
    const priorityDiff = priority[a.type] - priority[b.type];
    if (priorityDiff !== 0) return priorityDiff;

    if (a.type === 'text' && b.type === 'text') {
      const aTail = a.streamKind === 'tail' ? 1 : 0;
      const bTail = b.streamKind === 'tail' ? 1 : 0;
      return aTail - bTail;
    }

    return 0;
  });
}

function setText(content: RuntimeContentPart[], text: string) {
  const existing = findTextPart(content);
  if (!text) {
    if (!existing) return;
    content.splice(content.indexOf(existing), 1);
    return;
  }

  if (existing) {
    existing.text = text;
    return;
  }

  content.push({
    type: 'text',
    text,
  });
}

function setStreamText(content: RuntimeContentPart[], text: string) {
  const existing = findStreamTextPart(content);
  if (!text) {
    if (!existing) return;
    content.splice(content.indexOf(existing), 1);
    return;
  }

  if (existing) {
    existing.text = text;
    return;
  }

  content.push({
    type: 'text',
    text,
    streamKind: 'tail',
  });
}

function findPromotableTextBoundary(text: string) {
  let promoteIndex = 0;

  const paragraphBoundary = text.lastIndexOf('\n\n');
  if (paragraphBoundary !== -1) {
    promoteIndex = Math.max(promoteIndex, paragraphBoundary + 2);
  }

  const lineBoundary = text.lastIndexOf('\n');
  if (lineBoundary !== -1) {
    const completedLines = text.slice(0, lineBoundary + 1);
    if (/(^|\n)\s*(?:[-*+]\s|\d+\.\s|\|)/.test(completedLines)) {
      promoteIndex = Math.max(promoteIndex, lineBoundary + 1);
    }
  }

  const fenceMatches = [...text.matchAll(/```/g)];
  if (fenceMatches.length >= 2 && fenceMatches.length % 2 === 0) {
    const lastFence = fenceMatches[fenceMatches.length - 1];
    if (lastFence?.index != null) {
      promoteIndex = Math.max(promoteIndex, lastFence.index + 3);
    }
  }

  return promoteIndex;
}

/**
 * A turn the model stopped at its output limit (`finishReason: 'length'`).
 * With no text the user would see an empty reply, so the whole message says
 * it was too long (cards it already made stay above it); after part of a
 * reply a closing line says where it stopped. The note is client copy in the
 * reader's language and is not stored, so a reload shows the turn as saved.
 */
export function withTruncationNotice(text: string): string {
  const partial = text.trim().length > 0;
  const notice = translate(partial ? 'chat.errors.truncatedPartial' : 'chat.errors.truncated');
  if (typeof notice !== 'string' || !notice) return text;
  return partial ? `${text.trimEnd()}\n\n${notice}` : notice;
}

function isAbortLikeError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === 'AbortError';
  }

  if (error && typeof error === 'object' && 'name' in error) {
    return typeof error.name === 'string' && error.name === 'AbortError';
  }

  return false;
}

export function createMastraChatModel(params: {
  apiUrl: string;
  fetchImpl: typeof fetch;
  mode: AgentMode;
  companyId?: string;
  textBatchIntervalMs?: number;
  threadIdRef: MutableRefObject<string | undefined>;
  onThreadCreatedRef: MutableRefObject<((id: string) => void) | undefined>;
  /**
   * Refreshes thread metadata after persistence has finished. The `start`
   * event is intentionally too early for this: a new row can exist before
   * its title and `lastMessageAt` have been committed.
   */
  onThreadSettledRef?: MutableRefObject<(() => void) | undefined>;
  /**
   * Read at send time (same latch pattern as threadIdRef) so the envelope
   * describes the screen the user is on NOW. Web-only: the native runtime
   * does not pass it, so native requests are byte-identical to before.
   */
  getPageContext?: () => PageContextEnvelope | null;
  /**
   * The composer's model pick, read at send time for the same reason as
   * getPageContext: the turn runs on whatever was selected when Send was hit,
   * even if the user changes it mid-stream. Web-only; native omits it and
   * keeps today's plan routing.
   */
  getModelId?: () => string | undefined;
  /**
   * The composer's Research, Image and Result controls, read at send time for
   * the same reason as getModelId: the turn runs with what was picked when
   * Send was hit. Returns undefined where the controls do not apply
   * (employer threads), and the request then carries no capabilities at all.
   */
  getCapabilities?: () => AgentChatCapabilities | undefined;
  /**
   * The composer's `@` mentions (`@Web search`, `@Document`...), read at send
   * time from the text of the message. Returns the text without its mentions
   * and the capabilities for this one turn, or null when the message has
   * none, in which case it goes out as typed with getCapabilities().
   * `hasAttachments` says the message carries files, so mentions with no
   * other text still apply (the phone's chips on an attachment-only message).
   */
  resolveMentions?: (
    text: string,
    context: { hasAttachments: boolean },
  ) => { text: string; capabilities: AgentChatCapabilities } | null;
  /**
   * Invoked once per toolCallId when a tool-result arrives on the LIVE
   * stream. Hydrated history goes through convertToThreadMessages →
   * initialMessages and never re-enters run(), so old threads cannot
   * re-trigger this. Web-only: used to drive content-panel refreshes.
   */
  onLiveToolResult?: (activity: AgentToolActivity) => void;
  /**
   * Called once per reply, on the first `start` of a streaming writer build
   * (plan 7.2). Web uses it to dock the canvas panel on a wide screen; native
   * passes nothing. `threadId` is the thread this reply belongs to, so the
   * caller can tell whether it is still the thread on screen.
   */
  onArtifactDraftStart?: (
    event: ArtifactDraftEvent,
    context: { replyStartedAt: number; threadId: string | undefined },
  ) => void;
  /**
   * The file this message works on (plan 5.3, 7.10), read at send time with
   * the thread the message goes to: a thread bound to a site sends its page.
   */
  getArtifactContext?: (threadId: string | undefined) => (ArtifactContext & { site?: boolean }) | undefined;
  /**
   * Sites mode (plan 7.10), read and cleared at send time: the message asks
   * for a page, and its `createPage` result becomes a draft site through
   * `onSitePageCreated`.
   */
  consumeSiteMode?: () => boolean;
  onSitePageCreated?: (artifactId: string, threadId: string | undefined) => void;
}): ChatModelAdapter {
  return {
    async *run(options) {
      type StreamRaceResult = { kind: 'event'; result: IteratorResult<AgentStreamEvent> } | { kind: 'flush' };

      const lastUserMessage = options.messages.filter((message) => message.role === 'user').pop();

      // Extract text
      const textPart = lastUserMessage?.content?.find((p: any) => p.type === 'text');
      const text = textPart && 'text' in textPart ? (textPart as any).text : '';

      // The one place every user turn passes through: native composer, web
      // composer, quick prompts and tool-card prompts. Counts, never content.
      trackProductEvent('chat_message_sent', {
        mode: params.mode,
        textLength: typeof text === 'string' ? text.length : 0,
        hasCompany: Boolean(params.companyId),
      });

      // Extract attachments from message.attachments[].content[] (assistant-ui keeps them separate from content)
      const attachments: Array<{
        type: 'image' | 'file';
        name?: string;
        mimeType: string;
        data: string;
      }> = [];

      const msgAttachments = (lastUserMessage as any)?.attachments as any[] | undefined;
      if (msgAttachments?.length) {
        for (const att of msgAttachments) {
          const contentParts = att.content as any[] | undefined;
          if (!contentParts?.length) continue;

          for (const part of contentParts) {
            if (part.type === 'image' && typeof part.image === 'string') {
              const imageData = part.image;
              const base64 = imageData.includes(',') ? imageData.split(',')[1]! : imageData;
              const mimeMatch = imageData.match(/^data:([^;]+);/);
              attachments.push({
                type: 'image',
                name: att.name,
                mimeType: mimeMatch?.[1] ?? att.contentType ?? 'image/jpeg',
                data: base64,
              });
            } else if (part.type === 'file' && part.data) {
              attachments.push({
                type: 'file',
                name: att.name,
                mimeType: part.mimeType ?? att.contentType ?? 'application/octet-stream',
                data: part.data,
              });
            } else if (part.type === 'text' && part.text) {
              // SimpleTextAttachmentAdapter produces text content: include as file
              attachments.push({
                type: 'file',
                name: att.name,
                mimeType: att.contentType ?? 'text/plain',
                data: btoa(part.text),
              });
            }
          }
        }
      }

      if (!text && attachments.length === 0) {
        return;
      }

      const hadThreadBefore = Boolean(params.threadIdRef.current);
      const hasAssistantMessages = options.messages.some((message) => message.role === 'assistant');
      if (!hasAssistantMessages && !params.threadIdRef.current) {
        params.threadIdRef.current = undefined;
      }

      const content: RuntimeContentPart[] = [];
      let markdownText = '';
      let liveTailText = '';
      let pendingText = '';
      let flushTimer: ReturnType<typeof setTimeout> | null = null;
      let flushPromise: Promise<{ kind: 'flush' }> | null = null;
      const textBatchIntervalMs = Math.max(0, params.textBatchIntervalMs ?? 0);
      const shouldBatchText = textBatchIntervalMs > 0;
      let receivedTerminalEvent = false;
      let notifiedThreadSettled = false;

      const notifyThreadSettled = () => {
        if (notifiedThreadSettled) return;
        notifiedThreadSettled = true;

        try {
          params.onThreadSettledRef?.current?.();
        } catch {
          // Thread-list freshness must never break the terminal chat frame.
        }
      };

      const syncTextContent = () => {
        setText(content, markdownText);
        setStreamText(content, liveTailText);
        return sortedContent(content);
      };

      const promoteLiveTailToMarkdown = (count = liveTailText.length) => {
        if (count <= 0) return;
        markdownText += liveTailText.slice(0, count);
        liveTailText = liveTailText.slice(count);
      };

      const flushPendingText = () => {
        if (!pendingText) return null;

        liveTailText += pendingText;
        pendingText = '';

        const promoteIndex = findPromotableTextBoundary(liveTailText);
        if (promoteIndex > 0) {
          promoteLiveTailToMarkdown(promoteIndex);
        }

        bumpNativeAgentDebugCounter('adapterYields', {
          reason: 'text-delta',
          chars: markdownText.length + liveTailText.length,
          liveTailChars: liveTailText.length,
          parts: content.length,
        });

        return syncTextContent();
      };

      const commitLiveText = () => {
        if (!pendingText && !liveTailText) return null;

        if (pendingText) {
          liveTailText += pendingText;
          pendingText = '';
        }

        promoteLiveTailToMarkdown();
        return syncTextContent();
      };

      const clearTextFlushTimer = () => {
        if (flushTimer != null) {
          clearTimeout(flushTimer);
          flushTimer = null;
        }
        flushPromise = null;
      };

      const queuePendingTextFlush = () => {
        if (!shouldBatchText || !pendingText || flushPromise) return;

        flushPromise = new Promise<{ kind: 'flush' }>((resolve) => {
          flushTimer = setTimeout(() => {
            flushTimer = null;
            flushPromise = null;
            resolve({ kind: 'flush' });
          }, textBatchIntervalMs);
        });
      };

      // Latched at call time alongside threadIdRef, so "this job"/"this CV"
      // resolves against whatever the content panel shows when Send is hit.
      const pageContext = params.getPageContext?.() ?? undefined;
      const modelId = params.getModelId?.() ?? undefined;
      const mentioned =
        typeof text === 'string' && text
          ? params.resolveMentions?.(text, { hasAttachments: attachments.length > 0 }) ?? null
          : null;
      const siteMode = params.consumeSiteMode?.() ?? false;
      const requested = mentioned
        ? mentioned.capabilities
        : params.getCapabilities?.() ?? undefined;
      // Sites mode always asks for a page, whatever the other controls say.
      const capabilities = siteMode ? { ...(requested ?? {}), artifact: 'page' as const } : requested;
      const artifactContext = params.getArtifactContext?.(params.threadIdRef.current);
      const message = mentioned ? mentioned.text : text;
      // Read per message: a phone can cross a timezone between two turns.
      const timezone = resolveDeviceTimezone();

      // Live-stream tool results only, deduped per toolCallId: the stream can
      // upsert the same call several times (args, then result) but the panel
      // must react exactly once.
      const emittedPanelToolCallIds = new Set<string>();
      // Writer builds of this reply: their drafts live in the draft store,
      // outside the message content, and read as stopped if the stream ends
      // before they do.
      const replyStartedAt = Date.now();
      const draftToolCallIds = new Set<string>();
      // Agent edits of this reply: the open file says Editing until the new
      // version is its head; an edit with no result when the reply ends is over.
      const editToolCallIds = new Set<string>();
      let draftStartAnnounced = false;
      let replyThreadId = params.threadIdRef.current;

      try {
        const iterator = streamAgentChat({
          fetchImpl: params.fetchImpl,
          apiUrl: params.apiUrl,
          message,
          threadId: params.threadIdRef.current,
          mode: params.mode,
          companyId: params.companyId,
          abortSignal: options.abortSignal,
          attachments: attachments.length > 0 ? attachments : undefined,
          pageContext,
          modelId,
          capabilities,
          timezone,
          artifactContext,
        })[Symbol.asyncIterator]();

        let nextEventPromise: Promise<{
          kind: 'event';
          result: IteratorResult<AgentStreamEvent>;
        }> = iterator.next().then((result) => ({ kind: 'event', result }));

        while (true) {
          const winner: StreamRaceResult =
            flushPromise != null ? await Promise.race<StreamRaceResult>([nextEventPromise, flushPromise]) : await nextEventPromise;

          if (winner.kind === 'flush') {
            const nextContent = flushPendingText();
            if (nextContent) {
              yield { content: nextContent };
            }
            continue;
          }

          const { result } = winner;
          if (result.done) {
            stopArtifactDrafts(draftToolCallIds);
            stopArtifactEdits(editToolCallIds);
            clearTextFlushTimer();
            const nextContent = flushPendingText();
            if (nextContent) {
              yield { content: nextContent };
            }
            if (!receivedTerminalEvent) {
              // Defensive terminal state for a proxy/server that closes an SSE
              // body without `finish`. Without this, assistant-ui keeps the
              // composer in Stop and queues later prompts behind a dead turn.
              yield {
                content: sortedContent(content),
                status: { type: 'complete', reason: 'stop' },
              };
            }
            notifyThreadSettled();
            return;
          }

          const event = result.value;
          nextEventPromise = iterator.next().then((nextResult) => ({
            kind: 'event',
            result: nextResult,
          }));

          // A writer draft never touches the message content: no yield, so a
          // code delta cannot re-render the thread. Only the building card and
          // the canvas panel of that build read it, from the draft store.
          if (event.type === 'artifact-draft') {
            const applied = applyArtifactDraftEvent(event.payload);
            if (applied) {
              draftToolCallIds.add(applied.toolCallId);
              if (event.payload.phase === 'start' && !draftStartAnnounced) {
                draftStartAnnounced = true;
                try {
                  params.onArtifactDraftStart?.(event.payload, {
                    replyStartedAt,
                    threadId: replyThreadId,
                  });
                } catch {
                  // Opening the panel must never break the chat stream.
                }
              }
            }
            continue;
          }

          if (event.type !== 'text-delta' && (pendingText || liveTailText)) {
            clearTextFlushTimer();
            const nextContent = commitLiveText();
            if (nextContent) {
              yield { content: nextContent };
            }
          }

          switch (event.type) {
            case 'start': {
              logNativeAgentDebug('chat-model-start', {
                threadId: event.payload.threadId,
                mode: params.mode,
              });
              params.threadIdRef.current = event.payload.threadId;
              replyThreadId = event.payload.threadId;
              // What this message attached stays on screen after a history
              // read that has no attachments for it yet (lib/agent-attachments).
              rememberSentAttachments(event.payload.threadId, message, sentMessageAttachments(msgAttachments));
              if (!hadThreadBefore && params.onThreadCreatedRef.current) {
                params.onThreadCreatedRef.current(event.payload.threadId);
              }
              break;
            }

            case 'text-delta': {
              if (!shouldBatchText) {
                markdownText += event.payload.text;
                bumpNativeAgentDebugCounter('adapterYields', {
                  reason: 'text-delta',
                  chars: markdownText.length,
                  parts: content.length,
                });
                yield { content: syncTextContent() };
                break;
              }

              pendingText += event.payload.text;
              queuePendingTextFlush();
              break;
            }

            case 'tool-call':
            case 'tool-call-approval':
            case 'tool-result': {
              const activity = buildToolActivity({
                eventType: event.type,
                payload: event.payload,
              });
              upsertToolPart(content, activity);
              if (event.type === 'tool-call') {
                if (noteArtifactEditCall(activity.toolCallId, activity.toolName, activity.args)) {
                  editToolCallIds.add(activity.toolCallId);
                }
              }
              if (event.type === 'tool-result') {
                markArtifactDraftResult(activity.toolCallId);
                noteArtifactEditResult(
                  activity.toolCallId,
                  activity.result,
                  Boolean(activity.isError) || activity.status === 'failed',
                );
              }
              if (siteMode && event.type === 'tool-result') {
                const artifactId = sitePageArtifactId(activity.toolName, activity.result);
                if (artifactId) {
                  try {
                    params.onSitePageCreated?.(artifactId, params.threadIdRef.current);
                  } catch {
                    // A draft site must never break the chat stream.
                  }
                }
              }

              if (event.type === 'tool-result' && params.onLiveToolResult && !emittedPanelToolCallIds.has(activity.toolCallId)) {
                emittedPanelToolCallIds.add(activity.toolCallId);
                try {
                  const mergedToolPart = content.find(
                    (part): part is RuntimeToolCallPart => part.type === 'tool-call' && part.toolCallId === activity.toolCallId,
                  );
                  // Tool-result payloads are allowed to omit args. The card
                  // already merges them from the preceding tool-call; give
                  // panel sync that same complete activity so job filters do
                  // not silently collapse to empty values.
                  params.onLiveToolResult(
                    mergedToolPart
                      ? {
                          ...activity,
                          args: mergedToolPart.args,
                          argsText: mergedToolPart.argsText,
                        }
                      : activity,
                  );
                } catch {
                  // A panel-sync subscriber must never break the chat stream.
                }
              }

              bumpNativeAgentDebugCounter('adapterYields', {
                reason: event.type,
                toolName: activity.toolName,
                parts: content.length,
              });
              yield { content: sortedContent(content) };
              break;
            }

            case 'error': {
              const nextContent = commitLiveText();
              if (nextContent) {
                yield { content: nextContent };
              }

              markdownText += `\n\n[Error: ${event.payload.message}]`;
              bumpNativeAgentDebugCounter('adapterYields', {
                reason: 'error',
                chars: markdownText.length,
              });
              yield { content: syncTextContent() };
              break;
            }

            case 'finish': {
              receivedTerminalEvent = true;
              clearTextFlushTimer();
              stopArtifactEdits(editToolCallIds);

              if (event.payload.text) {
                markdownText = event.payload.text;
                liveTailText = '';
                pendingText = '';
              } else {
                commitLiveText();
              }

              if (event.payload.finishReason === 'length') {
                markdownText = withTruncationNotice(markdownText);
              }

              bumpNativeAgentDebugCounter('adapterYields', {
                reason: 'finish',
                chars: event.payload.text?.length ?? markdownText.length,
                parts: content.length,
              });
              notifyThreadSettled();
              yield {
                content: syncTextContent(),
                status: { type: 'complete', reason: 'stop' },
                // The rendered message's own `.id` is an ephemeral client
                // nanoid minted before this adapter ever runs and can't be
                // renamed - thread the real backend-persisted id through
                // metadata.custom instead, which assistant-ui preserves.
                // Feedback controls read it back via useAuiState.
                ...(event.payload.messageId
                  ? {
                      metadata: {
                        custom: {
                          messageId: event.payload.messageId,
                          threadId: event.payload.threadId,
                        },
                      },
                    }
                  : {}),
              };
              return;
            }
          }
        }
      } catch (error) {
        stopArtifactDrafts(draftToolCallIds);
        stopArtifactEdits(editToolCallIds);
        clearTextFlushTimer();
        const nextContent = commitLiveText();
        if (nextContent) {
          yield { content: nextContent };
        }

        if (isAbortLikeError(error)) {
          // The cleanest proxy for a slow or wrong answer: the user stopped it.
          trackProductEvent('chat_generation_stopped', {});
          notifyThreadSettled();
          yield {
            content: sortedContent(content),
            status: { type: 'incomplete', reason: 'cancelled' },
          };
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        // Agent reliability is the core product; only the bucket leaves the device.
        trackProductEvent('chat_response_failed', {
          error_kind: classifyChatError(errorMessage),
        });
        markdownText += `\n\nI ran into an error contacting the agent: ${errorMessage}`;
        logNativeAgentDebug('chat-model-catch', { errorMessage });
        notifyThreadSettled();
        yield {
          content: syncTextContent(),
          status: { type: 'complete', reason: 'stop' },
        };
      }
    },
  };
}
